import React, { useState } from 'react';
import { Box, IconButton, Skeleton, Typography, useTheme, styled } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import RefreshIcon from '@mui/icons-material/Refresh';
import CarouselCardMAIQ from './CarouselCardMAIQ';

export interface CarouselCardData {
  _id: string | number;
  content: string; // Content with markdown-like syntax
  email?: string;
  onClick?: () => void;
}

interface CarouselMAIQProps {
  title?: string;
  data: CarouselCardData[];
  isLoading?: boolean;
  onCardClick?: (card: CarouselCardData) => void;
  isRefetching?: boolean;
  lastUpdatedAt?: number;
  onRefresh?: () => void;
  cardHeight?: number;
}

const CARDS_VISIBLE = 5;

const CarouselContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  border: `1px solid ${theme.palette.divider}`,
}));

const CarouselHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
}));

const CarouselTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

const CarouselContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  overflow: 'hidden',
}));

const CarouselCardContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  transition: 'transform 0.3s ease-in-out',
  minWidth: 'fit-content',
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:disabled': {
    opacity: 0.5,
    backgroundColor: theme.palette.action.disabledBackground,
  },
}));

const RefreshButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
}));

const CarouselMAIQ: React.FC<CarouselMAIQProps> = ({ 
  title = '', 
  data, 
  isLoading = false, 
  onCardClick,
  isRefetching = false,
  lastUpdatedAt,
  onRefresh,
  cardHeight = 200
}) => {
  const theme = useTheme();
  const [startIdx, setStartIdx] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [cardsVisible, setCardsVisible] = useState(CARDS_VISIBLE);

  // Add resize listener to update cards visible
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 600) {
        setCardsVisible(1);
      } else if (window.innerWidth < 900) {
        setCardsVisible(2);
      } else if (window.innerWidth < 1200) {
        setCardsVisible(3);
      } else if (window.innerWidth < 1500) {
        setCardsVisible(4);
      } else {
        setCardsVisible(CARDS_VISIBLE);
      }
    };

    handleResize(); // Initial call
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const maxStartIdx = Math.max(0, data.length - cardsVisible);
  const endIdx = Math.min(startIdx + cardsVisible, data.length);

  const handlePrev = () => {
    if (startIdx > 0 && !isAnimating) {
      setIsAnimating(true);
      setStartIdx(prev => Math.max(0, prev - 1));
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  const handleNext = () => {
    if (startIdx < maxStartIdx && !isAnimating) {
      setIsAnimating(true);
      setStartIdx(prev => Math.min(maxStartIdx, prev + 1));
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  const handleCardClick = (card: CarouselCardData) => {
    if (onCardClick) {
      onCardClick(card);
    }
  };

  const formatLastUpdated = () => {
    if (!lastUpdatedAt) return '';
    const date = new Date(lastUpdatedAt);
    return date.toLocaleTimeString();
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <CarouselContainer>
        <CarouselHeader>
          <CarouselTitle>{title}</CarouselTitle>
          {onRefresh && (
            <RefreshButton size="small">
              <RefreshIcon />
            </RefreshButton>
          )}
        </CarouselHeader>
        <CarouselContent>
          {Array.from({ length: cardsVisible }).map((_, index) => (
            <Skeleton 
              key={index} 
              variant="rectangular" 
              width="100%" 
              height={cardHeight}
              sx={{ borderRadius: theme.shape.borderRadius }}
            />
          ))}
        </CarouselContent>
      </CarouselContainer>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    return (
      <CarouselContainer>
        <CarouselHeader>
          <CarouselTitle>{title}</CarouselTitle>
          {onRefresh && (
            <RefreshButton size="small">
              <RefreshIcon />
            </RefreshButton>
          )}
        </CarouselHeader>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            No data available
          </Typography>
        </Box>
      </CarouselContainer>
    );
  }

  return (
    <CarouselContainer>
      <CarouselHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CarouselTitle>{title}</CarouselTitle>
          {isRefetching && (
            <RefreshIcon 
              sx={{ 
                fontSize: 16, 
                color: 'primary.main',
                animation: 'spin 1s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }} 
            />
          )}
        </Box>
        {onRefresh && (
          <RefreshButton 
            size="small" 
            onClick={onRefresh}
            title={lastUpdatedAt ? `Last updated: ${formatLastUpdated()}` : 'Refresh'}
          >
            <RefreshIcon />
          </RefreshButton>
        )}
      </CarouselHeader>
      
      <CarouselContent>
        <NavigationButton 
          onClick={handlePrev}
          disabled={startIdx === 0 || isAnimating}
          size="small"
        >
          <ArrowBackIosNewIcon fontSize="small" />
        </NavigationButton>
        
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <CarouselCardContainer
            sx={{
              transform: `translateX(-${(startIdx / cardsVisible) * 100}%)`,
            }}
          >
            {data.slice(startIdx, endIdx).map((card, index) => (
              <Box 
                key={card._id} 
                sx={{ 
                  minWidth: `calc(${100 / cardsVisible}% - ${theme.spacing(2)})`,
                  flex: '0 0 auto',
                }}
              >
                <CarouselCardMAIQ
                  content={card.content}
                  onClick={() => handleCardClick(card)}
                  height={cardHeight}
                />
              </Box>
            ))}
          </CarouselCardContainer>
        </Box>
        
        <NavigationButton 
          onClick={handleNext}
          disabled={startIdx >= maxStartIdx || isAnimating}
          size="small"
        >
          <ArrowForwardIosIcon fontSize="small" />
        </NavigationButton>
      </CarouselContent>
    </CarouselContainer>
  );
};

export default CarouselMAIQ;
